<!-- Your app's shell. THIS FILE IS YOURS — the factory never replaces it.
     Everything imported from ./core is maintained by `appfactory upgrade`.

     This file is now DELIBERATELY THIN. It is the root: an IonApp, the outlet
     the router paints into, and the handful of things that must exist on every
     screen at once — the demo banner, onboarding, toasts, live regions.

     The screens themselves live in src/pages/, wired up in src/router.js, and
     the state they share is src/store.js. That split is the whole reason the
     chassis moved to Ionic's router: pages that mount and unmount get real
     push/pop transitions and a per-tab back stack, which a single component
     switching on a `current` string cannot have.

     WHERE TO START:
       src/router.js      your tabs, and what each one opens
       src/pages/         one file per screen — replace the worked example
       src/store.js       the data those screens read and write
       src/styles/app.css your own CSS, loaded last and never overwritten

     The worked example runs as-is. REPLACE THE CONTENT AND KEEP THE DENSITY:
     whatever is in pages/ is what the next app looks like. -->
<template>
  <IonApp>
    <!-- Says, permanently, that this is not your data.
         Outside the router outlet on purpose, so it shows on every screen
         rather than only the one that happened to render it. The chassis used
         to put this inside its own AppShell — the Ionic port dropped that
         component, the banner silently stopped rendering, and the demo no
         longer said it was a demo. That is the one thing core/demo.js insists
         on: sample data that looks like your own is a support ticket. -->
    <DemoBanner v-if="isDemo" />

    <IonRouterOutlet />

    <!-- Three or four cards, purpose and how-to-use. Shown once, ever.
         Give it an empty array and nothing renders at all. -->
    <!-- v2 because v1 was not merely dull, it was false — it promised sync in
         an app that has none. Anyone who read v1 was told something untrue, so
         everyone sees these once. The last card's button names the first real
         thing to do, which on the shelf is adding the person you are recording. -->
    <Onboarding :cards="onboarding" :version="2" finish-label="Add a cook" />

    <!-- Toasts, the undo bar, and the screen-reader live regions.
         Mounted once, here. Nothing imports it to use it. -->
    <Overlays />
  </IonApp>
</template>

<script setup>
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { Onboarding, DemoBanner, Overlays, isDemo } from './core/ui';

/* THE FIRST THREE SENTENCES OF THIS APP, AND THEY HAVE TO BE TRUE.
 *
 * These were the factory's stock cards: "Changes sync when you are back" and
 * "New versions arrive quietly". There is no sync — nothing in this app leaves
 * the device, which Keeping says in as many words — so the app's opening claim
 * was a lie, and the second card described a mechanism nobody opened this to
 * hear about.
 *
 * Replaced with what it actually does, in the register the rest of the app
 * uses: the way you talk in someone else's kitchen. Each card is one promise,
 * about the cooking, in plain words. The feeling is already in the recording —
 * it is her voice — and an interface adding its own on top is what would make
 * this app embarrassing to hand to your mother. So no memories, no legacy, no
 * heritage, nothing about running out of time. The welcome card went too: an
 * app that greets you before it tells you anything has spent its first card on
 * itself.
 */
const onboarding = [
  {
    title: 'Record while they cook',
    body: 'Sit down in the kitchen and hit record. What they say lands beside the step they were on when they said it.',
  },
  {
    title: 'A handful is a measurement',
    body: 'Write the amount the way they say it. 10 kg is read as 10 kg, and "until it looks right" is kept in those words.',
  },
  {
    title: 'It stays on this device',
    body: 'The recipes and the recordings are kept here — nothing is uploaded and nothing syncs. It opens and plays back with no signal.',
  },
];
</script>

